import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getFloorPlanUrl } from '@/services/floorPlanService';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryKeys';
import { useFixturesStandardized } from '@/hooks/entities/useFixturesStandardized';
import { useCircuitsStandardized } from '@/hooks/entities/useCircuitsStandardized';
import { useControlsStandardized } from '@/hooks/entities/useControlsStandardized';
import { useRoomsStandardized } from '@/hooks/entities/useRoomsStandardized';
import { usePanelsStandardized } from '@/hooks/entities/usePanelsStandardized';
import { useLVProducts } from '@/features/product-library/hooks/useLVProducts';
import { useAnnotations } from '@/hooks/useAnnotations';
import { useFloorPlanMetadata } from '@/hooks/useFloorPlanMetadata';
import { useWalls } from '@/hooks/useWalls';
import { logger } from '@/lib/logger';

export function useFloorPlanData(floorPlanId: string) {
  // First, fetch the floor plan details to get the file_path
  const { data: floorPlan } = useQuery({
    queryKey: queryKeys.floorPlan(floorPlanId),
    queryFn: async () => {
      const authSupabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await authSupabase
        .from('floor_plans')
        .select('*')
        .eq('id', floorPlanId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!floorPlanId,
  });

  // Fetch DWG metadata if this is a CAD file
  const { data: dwgMetadata } = useQuery({
    queryKey: [...queryKeys.floorPlan(floorPlanId), 'dwg-metadata'],
    queryFn: async () => {
      const authSupabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await authSupabase
        .from('floor_plan_dwg_metadata')
        .select('*')
        .eq('floor_plan_id', floorPlanId)
        .single();

      if (error && error.code !== 'PGRST116') {
        // Ignore "not found" errors
        logger.error('Error fetching DWG metadata:', error);
      }

      // Debug logging
      logger.log('DWG Metadata Query Result:', {
        floorPlanId,
        isCadFile: floorPlan?.is_cad_file,
        metadata: data,
        svgUrl: data?.svg_url,
        error,
      });

      if (!floorPlan?.is_cad_file) {
        logger.warn('Floor plan is_cad_file flag is not true:', floorPlan);
      }

      return data;
    },
    // Enable if we have a floor plan ID and either:
    // 1. is_cad_file is true, OR
    // 2. file_type is SVG (CAD files are stored as SVG)
    enabled: !!floorPlanId && (floorPlan?.is_cad_file || floorPlan?.file_type === 'image/svg+xml'),
  });

  // Fetch floor plan image URL
  // For CAD files, we'll use the SVG URL from DWG metadata
  // For regular files, use the file_path
  const { data: imageUrl } = useQuery({
    // Include file_path in query key so cache invalidates when floor plan image is replaced
    queryKey: [
      ...queryKeys.floorPlan(floorPlanId),
      'url',
      floorPlan?.file_path,
      dwgMetadata?.svg_url,
    ],
    queryFn: async () => {
      // If we have DWG metadata with SVG URL, use that (it's already a full URL)
      if (dwgMetadata?.svg_url) {
        logger.log('Using SVG URL from DWG metadata:', dwgMetadata.svg_url);
        return dwgMetadata.svg_url;
      }

      // Otherwise, use the file_path
      if (!floorPlan?.file_path) {
        logger.log('No file_path in floor plan:', floorPlan);
        return '';
      }
      logger.log('Getting URL for file_path:', floorPlan.file_path);
      const url = getFloorPlanUrl(floorPlan.file_path, floorPlan.updated_at);
      logger.log('Generated URL:', url);
      return url || '';
    },
    enabled: !!floorPlanId && (!!floorPlan?.file_path || !!dwgMetadata?.svg_url),
  });

  // Use standardized hooks for all entities
  const {
    fixtures,
    isLoading: fixturesLoading,
    error: fixturesError,
    refetch: refetchFixtures,
    createFixture,
    updateFixture,
    deleteFixture,
    isCreating: isCreatingFixture,
    isUpdating: isUpdatingFixture,
    isDeleting: isDeletingFixture,
  } = useFixturesStandardized(floorPlanId);

  const {
    circuits,
    isLoading: circuitsLoading,
    refetch: refetchCircuits,
  } = useCircuitsStandardized(floorPlanId);

  const {
    controls,
    isLoading: controlsLoading,
    error: controlsError,
    refetch: refetchControls,
    createControl,
    updateControl,
    deleteControl,
    isCreating: isCreatingControl,
    isUpdating: isUpdatingControl,
    isDeleting: isDeletingControl,
  } = useControlsStandardized(floorPlanId);

  // Debug logging for controls
  if (controls) {
    logger.log('Controls loaded:', controls.length, 'controls');
  }
  if (controlsError) {
    logger.error('Controls error:', controlsError);
  }

  const {
    rooms,
    isLoading: roomsLoading,
    refetch: refetchRooms,
  } = useRoomsStandardized(floorPlanId);

  const { walls, isLoading: wallsLoading, refetch: refetchWalls } = useWalls(floorPlanId);

  const {
    panels,
    isLoading: panelsLoading,
    refetch: refetchPanels,
  } = usePanelsStandardized(floorPlanId);

  const {
    products: lvProducts,
    isLoading: lvProductsLoading,
    refetch: refetchLVProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    isCreating: isCreatingProduct,
    isUpdating: isUpdatingProduct,
    isDeleting: isDeletingProduct,
  } = useLVProducts(floorPlanId);

  const {
    annotations,
    isLoading: annotationsLoading,
    updateAnnotation,
    deleteAnnotation,
    refetch: refetchAnnotations,
  } = useAnnotations(floorPlanId);

  // Use floor plan metadata hook
  const {
    floorPlanMetadata,
    saveFloorPlanMetadata,
    saveCategoryDefault,
    getCategoryDefault,
    handleImageLoad,
    isLoading: isMetadataLoading,
  } = useFloorPlanMetadata(floorPlanId);

  const isLoading =
    fixturesLoading ||
    circuitsLoading ||
    controlsLoading ||
    roomsLoading ||
    wallsLoading ||
    panelsLoading ||
    lvProductsLoading ||
    annotationsLoading;

  // Log any errors
  if (fixturesError) logger.error('Error loading fixtures:', fixturesError);
  if (controlsError) logger.error('Error loading controls:', controlsError);

  // Combined refetch function
  const refetchAll = async () => {
    await Promise.all([
      refetchFixtures(),
      refetchCircuits(),
      refetchControls(),
      refetchRooms(),
      refetchWalls(),
      refetchPanels(),
      refetchLVProducts(),
      refetchAnnotations?.(),
    ]);
  };

  // Memoize refetchIndividual to prevent infinite re-renders
  const refetchIndividual = useMemo(
    () => ({
      fixtures: refetchFixtures,
      circuits: refetchCircuits,
      controls: refetchControls,
      rooms: refetchRooms,
      walls: refetchWalls,
      panels: refetchPanels,
      products: refetchLVProducts,
      annotations: refetchAnnotations,
    }),
    [
      refetchFixtures,
      refetchCircuits,
      refetchControls,
      refetchRooms,
      refetchWalls,
      refetchPanels,
      refetchLVProducts,
      refetchAnnotations,
    ],
  );

  const result = {
    imageUrl,
    svgUrl: dwgMetadata?.svg_url, // SVG URL for CAD files
    fixtures: fixtures || [],
    circuits: circuits || [],
    controls: controls || [],
    rooms: rooms || [],
    walls: walls || [],
    panels: panels || [],
    lvProducts: lvProducts || [],
    annotations: annotations || [],
    updateAnnotation,
    deleteAnnotation,
    isLoading,
    refetch: refetchAll, // Combined refetch for all entities
    // Individual refetch functions for realtime collaboration (memoized to prevent re-renders)
    refetchIndividual,
    refetchProducts: refetchLVProducts, // Keep for backward compatibility with Portal drawer
    imageSize: { width: 0, height: 0 }, // Image size is set when the image loads in FloorPlanViewport
    floorPlan,
    floorPlanMetadata,
    saveFloorPlanMetadata,
    saveCategoryDefault,
    getCategoryDefault,
    handleImageLoad,
    isMetadataLoading,

    // Mutation functions with optimistic updates
    createFixture,
    updateFixture,
    deleteFixture,
    createControl,
    updateControl,
    deleteControl,
    createProduct,
    updateProduct,
    deleteProduct,

    // Mutation loading states
    isCreatingFixture,
    isUpdatingFixture,
    isDeletingFixture,
    isCreatingControl,
    isUpdatingControl,
    isDeletingControl,
    isCreatingProduct,
    isUpdatingProduct,
    isDeletingProduct,
  };

  // Debug what we're returning
  logger.log('useFloorPlanData returning:', {
    imageUrl,
    svgUrl: dwgMetadata?.svg_url,
    hasDwgMetadata: !!dwgMetadata,
    isCadFile: floorPlan?.is_cad_file,
  });

  return result;
}
